import { useEffect, useState } from "react";
import styled from "styled-components";
import Input from "./input";
import Select from "./InputSelect";
import TimePicker from "./timePicker";

const shiftTypes = [
    { value: "seguido", label: "Turno seguido" },
    { value: "partido", label: "Turno partido" },
];

const daysOfWeek = [
    { value: "Lunes", label: "Lunes" },
    { value: "Martes", label: "Martes" },
    { value: "Miércoles", label: "Miércoles" },
    { value: "Jueves", label: "Jueves" },
    { value: "Viernes", label: "Viernes" },
    { value: "Sábado", label: "Sábado" },
    { value: "Domingo", label: "Domingo" },
];

const Wrapper = styled.div`
  padding: 15px 0;
  width: 100%;
  max-width: 960px;
  margin: auto;
  font-family: "Poppins", sans-serif;
  background-color: #ffffff;
  color: #222;
  line-height: 1.5;
  margin-bottom: 60px;

  h3 {
    font-size: 18px;
    font-weight: 600;
  }

  p {
    cursor: pointer;
    color: #666;
    text-decoration: underline;
    margin-top: 10px;
    font-size: 0.9rem;
  }
`;

const CustomSelector = styled.div`
  margin-top: 2rem;
`;

const TurnoCard = styled.div`
  border: 1px solid #e1e6ef;
  padding: 20px 20px 0;
  border-radius: 10px;
  margin-bottom: 1.2rem;
  background: #fff;
  transition: all 0.25s ease;
`;

const TurnoBody = styled.div`
  margin-top: 1rem;
`;

const HorariosFlex = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.2rem;
  margin: 1.2rem 0;
`;

const BtnSave = styled.button`
  margin-top: 2.5rem;
  padding: 0.85rem 1.8rem;
  background-color: #ff0102;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1.05rem;
  font-weight: 600;
  transition: background-color 0.25s ease;
  margin-bottom: 20px;

  &:hover:not(:disabled) {
    background-color: #e30000;
  }

  &:disabled {
    background-color: #aac9f2;
    cursor: not-allowed;
  }
`;

const ErrorMsg = styled.div`
  color: #c62828;
  font-size: 0.87rem;
  margin-top: 0.5rem;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #f5c6cb;
`;

const SuccessMsg = styled.div`
  color: #2e7d32;
  font-weight: 600;
  margin-top: 1.2rem;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #c5e6cd;
`;

export default function ShiftSelector({ initialShifts = {}, onUpdate }) {
    const [shifts, setShifts] = useState({});
    const [selectedCustomDays, setSelectedCustomDays] = useState([]);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState("");

    useEffect(() => {
        const defaultShifts = daysOfWeek.reduce((acc, { value: day }) => {
            acc[day] = {
                type: "seguido",
                time1Start: "",
                time1End: "",
                time2Start: "",
                time2End: "",
                day,
            };
            return acc;
        }, {});

        const mergedShifts = { ...defaultShifts, ...initialShifts };
        setShifts(mergedShifts);

        const selectedDays = Object.keys(initialShifts || {}).filter((day) =>
            daysOfWeek.find((d) => d.value === day)
        );
        setSelectedCustomDays(selectedDays);
    }, [initialShifts]);

    const handleShiftChange = (day, field, value) => {
        setShifts((prev) => ({
            ...prev,
            [day]: { ...prev[day], [field]: value, day },
        }));
    };

    const toggleCustomDay = (day) => {
        setSelectedCustomDays((prev) =>
            prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
        );
    };

    const validateShift = (shift) => {
        const parse = (timeStr) => {
            const [h, m] = timeStr.split(":").map(Number);
            return h * 60 + m;
        };

        const t1s = parse(shift?.time1Start);
        const t1e = parse(shift?.time1End);

        if (!shift?.time1Start || !shift?.time1End)
            return "Faltan horarios del primer turno";

        if (t1s === t1e) return "Hora inicio y fin no pueden ser iguales";

        if (shift.type === "partido") {
            if (!shift?.time2Start || !shift?.time2End)
                return "Faltan horarios del segundo turno";

            const t2s = parse(shift.time2Start);
            const t2e = parse(shift.time2End);

            if (t2s === t2e)
                return "2da hora inicio y fin no pueden ser iguales";

            const overlaps = (s1, e1, s2, e2) => {
                const norm = (s, e) => (e > s ? [s, e] : [s, e + 1440]);
                const [a1, b1] = norm(s1, e1);
                const [a2, b2] = norm(s2, e2);
                return a1 < b2 && a2 < b1;
            };

            if (overlaps(t1s, t1e, t2s, t2e))
                return "Los turnos no deben solaparse";
        }

        return null;
    };



    const handleSave = () => {
        setErrors({});
        setSuccessMessage("");
        const newErrors = {};

        selectedCustomDays.forEach((day) => {
            const error = validateShift(shifts[day]);
            if (error) newErrors[day] = error;
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const filteredShifts = Object.keys(shifts)
            .filter((day) => selectedCustomDays.includes(day))
            .reduce((acc, day) => {
                acc[day] = shifts[day];
                return acc;
            }, {});

        onUpdate?.(filteredShifts);
        setSuccessMessage("¡Turnos guardados correctamente!");
    };

    const getWeekNumber = (date = new Date()) => {
        const target = new Date(date.valueOf());
        target.setHours(0, 0, 0, 0);
        target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7));
        const firstThursday = new Date(target.getFullYear(), 0, 4);
        firstThursday.setDate(
            firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7)
        );
        const weekNumber =
            1 + Math.round((target - firstThursday) / (7 * 24 * 60 * 60 * 1000));
        return weekNumber;
    };

    const renderShiftFields = (shift, onChange) => (
        <>
            <HorariosFlex>
                <TimePicker
                    label="Hora inicio"
                    value={shift?.time1Start || ""}
                    onChange={(v) => onChange("time1Start", v)}
                />
                <TimePicker
                    label="Hora fin"
                    value={shift?.time1End || ""}
                    onChange={(v) => onChange("time1End", v)}
                    minTime={shift?.time1Start}
                />
            </HorariosFlex>

            {shift?.type === "partido" && (
                <HorariosFlex>
                    <TimePicker
                        label="2da inicio"
                        value={shift?.time2Start || ""}
                        onChange={(v) => onChange("time2Start", v)}
                    />
                    <TimePicker
                        label="2da fin"
                        value={shift?.time2End || ""}
                        onChange={(v) => onChange("time2End", v)}
                        minTime={shift?.time2Start}
                    />
                </HorariosFlex>
            )}
        </>
    );

    return (
        <Wrapper>
            <h3>Semana {getWeekNumber()}</h3>

            <CustomSelector>
                {daysOfWeek.map(({ value: day }) => {
                    const shift = shifts[day];
                    return (
                        <TurnoCard key={day}>
                            <Input
                                type="checkbox"
                                checkLabel={day}
                                value={selectedCustomDays.includes(day)}
                                onChange={() => toggleCustomDay(day)}
                            />

                            {selectedCustomDays.includes(day) && (
                                <TurnoBody>
                                    <Select
                                        label="Tipo de turno"
                                        options={shiftTypes}
                                        value={shiftTypes.find((t) => t.value === shift.type)}
                                        onChange={(option) =>
                                            handleShiftChange(day, "type", option.value)
                                        }
                                    />
                                    {renderShiftFields(shift, (f, v) =>
                                        handleShiftChange(day, f, v)
                                    )}
                                    {errors[day] && <ErrorMsg>{errors[day]}</ErrorMsg>}
                                </TurnoBody>
                            )}
                        </TurnoCard>
                    );
                })}
            </CustomSelector>

            {errors.global && <ErrorMsg>{errors.global}</ErrorMsg>}
            {successMessage && <SuccessMsg>{successMessage}</SuccessMsg>}

            <BtnSave onClick={handleSave}>Guardar</BtnSave>
        </Wrapper>
    );
}